import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcD = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule D: Comprehensive Guide and Instructions"
          description="Comprehensive guide for Form 990 Schedule D. Learn to report financial transactions accurately. Expert tips for compliance & transparency."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-d/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule D</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Activities Outside the U.S. with TaxZerone
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your supplemental financial statement with TaxZerone
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schd`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-d/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule D at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule D
                        </strong>{' '}
                        is used to report
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          supplemental financial information,
                        </strong>{' '}
                        including
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          donor advised funds, conservation easements, art and
                          museum collections, escrow or custodial accounts,
                          endowment funds
                        </strong>
                        , and other required financial disclosures.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations that answer{' '}
                        <strong style={{ fontWeight: 600 }}>"Yes"</strong> to
                        the applicable questions in{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990
                        </Link>
                        ,
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Part IV (Checklist of Required Schedules), lines 6
                          through 12a,
                        </strong>{' '}
                        generally must complete and attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule D</strong>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule D reports on{' '}
                        <strong style={{ fontWeight: 600 }}>
                          endowment funds, donor advised funds, conservation
                          easements, collections, escrow accounts, liabilities,
                          and other supplemental financial information
                        </strong>{' '}
                        that supports the amounts reported on{' '}
                        <strong style={{ fontWeight: 600 }}>Form 990.</strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Applicable Parts:
                        </strong>{' '}
                        Organizations complete{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          only the sections that apply{' '}
                        </strong>
                        to their activities, assets, and financial reporting
                        requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule D?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule D?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schd" sx={{ color: '#0202ff' }}>
                          Schedule D Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule D filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule D?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule D, </strong>
                  <i>Supplemental Financial Statements</i> is a supplementary
                  schedule that accompanies the standard{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>{' '}
                  and is filed annually by tax-exempt and nonprofit
                  organizations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  This schedule aims to provide the required reporting for
                  donor-advised funds, conservation easements, certain art and
                  museum collections, escrow or custodial accounts or
                  arrangements, endowment funds, and supplemental financial
                  information.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule D?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  An exempt organization that answered “Yes” to any of lines 6
                  through 12a on Form 990,{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Part IV, <i>Checklist of Required Schedules</i>
                  </strong>
                  , must complete the appropriate part(s) of Schedule D and
                  attach the schedule to Form 990
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Choose TaxZerone to complete your Schedule D filing
                      requirements.
                    </strong>
                    <br />
                    Your journey to staying tax-compliant starts here!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schd`}
                  >
                    Get Started
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schd"
                >
                  Schedule D Filing Requirements
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  that{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990
                  </Link>{' '}
                  must complete and attach Schedule D return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule D filing requirements for
                  each part.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Organizations Maintaining Donor-Advised Funds or
                  Other Similar Funds or Accounts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-org-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schd-org-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-org-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part I. Organizations Maintaining Donor Advised Funds or Other Similar Funds or Accounts"
                    width={760}
                    height={321}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization acts as a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    sponsoring organization
                  </span>{' '}
                  that manages Donor Advised Funds (DAFs), you must complete
                  this section to report how those funds were handled during the
                  tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  A <span style={{ fontWeight: 600 }}>donor advised fund</span>{' '}
                  is generally a charitable fund or account where:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Contributions are linked to one or more specific donors,
                  </li>
                  <li>
                    The fund is legally owned and fully controlled by your
                    organization, and
                  </li>
                  <li>
                    The donor or a donor advisor can recommend how the money
                    should be invested or distributed as grants because they
                    made the donation.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even though donors may provide recommendations, the
                  organization must retain{' '}
                  <span style={{ fontWeight: 600 }}>
                    final legal authority and control
                  </span>{' '}
                  over all assets and decisions.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}> Note:</strong> A donor
                  advised fund isn’t limited only to accounts classified as
                  “funds” under standard accounting rules. If donor advisory
                  privileges exist, the arrangement may still qualify.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Funds That Are Not Considered Donor Advised Funds
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Some funds or accounts are{' '}
                  <span style={{ fontWeight: 600 }}>
                    not treated as donor advised funds
                  </span>
                  , even if donors are connected to them. These include:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Funds that distribute money only to one specific charity or
                    government organization.{' '}
                  </li>
                  <li>
                    Scholarship or grant programs where donors participate only
                    as committee members chosen by the organization and don’t
                    control grant decisions.
                  </li>
                  <li>
                    Funds for the{' '}
                    <span style={{ fontWeight: 600 }}>
                      Internal Revenue Service (IRS)
                    </span>{' '}
                    are specifically exempt because donors don’t control
                    advisory decisions or because the fund supports a single
                    charitable purpose.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When determining donor control, related persons such as family
                  members or entities controlled by the donor are also
                  considered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column Reporting Requirements
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  You’ll report information in two columns:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  All donors advised funds held at any time during the tax
                  year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Other similar funds or accounts where donors had advisory
                  privileges, but which don’t meet the donor’s advised fund
                  definition (including IRS-approved exceptions).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Enter the total number of donors advised funds your
                  organization maintained at the end of the tax year. If
                  applicable, also include the number of similar advisory funds
                  or accounts that are not classified as donor advised funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Report the total amount of contributions added during the
                  year. Include all donations received during the reporting
                  period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report the total amount distributed from these funds during
                  the year.{' '}
                </Typography>
                <Typography variant="body1">This includes: </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Grants paid to outside charitable organizations, and</li>
                  <li>
                    Transfers used for charitable activities within your
                    organization.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total ending balance or fair market value of all
                    donors advised funds and similar advisory accounts as of the
                    end of the tax year.
                  </li>
                  <li>
                    Your organization must confirm whether required written
                    notices were provided.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1">
                  Indicate whether donors and donor advisors were informed in
                  writing that:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Contributions made to donor advised funds become the
                    organization’s property, and
                  </li>
                  <li>
                    The organization has full legal control over those assets.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1">
                  Indicate whether donors, donor advisors, and grant recipients
                  were informed in writing that:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Grant funds must be used only for charitable purposes, and
                  </li>
                  <li>
                    Funds cannot provide personal benefit to donors, advisors,
                    or related parties.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II – Conservation Easements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D, Part II, lines 1–3, reporting details about conservation easements held and related compliance information."
                    width={760}
                    height={240}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete Part II only if your organization answered “Yes” to
                  conservation easement reporting in Form 990, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Along with conservation easements, you should also include
                  other real property interests that function in a similar way
                  under state law and are created to support conservation or
                  preservation purposes. Examples may include certain
                  restrictive covenants or equitable servitudes that permanently
                  protect land or property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Do not include utility easements in this section.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Select the box or boxes that best describe why your
                    organization held conservation easements during the tax
                    year.
                  </li>
                  <li>
                    Some easements may serve more than one purpose. For example,
                    a property might protect open space while also preserving
                    natural habitat or historical significance. In those
                    situations, choose every option that applies.
                  </li>
                  <li>
                    Your selection should reflect the primary conservation or
                    preservation goal of the easement, such as public access,
                    environmental protection, maintaining open land, or
                    protecting historically important property or structures.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Complete Lines 2a through 2d only if your organization
                    continues to hold one or more qualified conservation
                    easements on the last day of the tax year.{' '}
                  </li>
                  <li>
                    These questions help report the organization’s conservation
                    easement activity as it stood at year-end. Use information
                    from your official records to make sure the details entered
                    reflect the actual status of the easements at that time.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total number of conservation easements your
                    organization held as of the end of the tax year.{' '}
                  </li>
                  <li>
                    Count each easement separately based on your records. This
                    number should represent the exact total and should not be
                    estimated or rounded.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Report the total acreage covered by conservation easements
                    as of the end of the tax year.{' '}
                  </li>
                  <li>
                    Add together all land areas protected under each easement.
                    Do not include acreage connected to certified historic
                    structures when calculating this total.
                  </li>
                  <li>
                    If any property involves partial acreage, decimals may be
                    used. For example, two and one-half acres should be entered
                    as 2.5 acres.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter how many of the easements reported on Line 2a apply to
                    certified historic structures.{' '}
                  </li>
                  <li>
                    A certified historic structure generally includes buildings
                    listed in the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      National Register of Historic
                    </span>{' '}
                    Places, or structures officially recognized as historically
                    significant within a registered historic district.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  From the number reported on Line 2c, enter how many
                  conservation easements were acquired after July 25, 2006, and
                  are not connected to a structure listed in the National
                  Register.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total number of conservation easements that were
                  modified, transferred, released, extinguished, or terminated,
                  either fully or partially, during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <span style={{ fontWeight: 600 }}> Example:</span> If two
                  easements were modified and one easement was terminated during
                  the tax year, enter 3.
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    A modification includes any change to the easement terms.
                    Transfers involve assigning or disposing of the easement,
                    and releases or terminations occur when legal protection
                    ends. Explain each change in Part XIII.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part2-line4-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part2-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part2-line4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part II showing lines 4–9, where organizations report details about conservation easements and related documentation."
                    width={760}
                    height={190}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the number of different states where your organization
                  holds conservation easement properties during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether your organization has written policies
                  explaining how it will monitor easement properties, conduct
                  site inspections, and handle violations. If you answer “Yes,”
                  you must briefly summarize these policies in Part XIII, and
                  Confirm whether the same procedures are included in your
                  easement documents.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total number of hours your organization spent
                  during the year monitoring properties, visiting sites, and
                  handling any easement violations. Include time worked by
                  employees, volunteers, contractors, or agents.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total costs related to managing and protecting
                  easements, such as staff time, travel for inspections,
                  professional services, or legal expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if all façade easements accepted after July 25,
                  2006, protect the entire outside of historic buildings and
                  include a written agreement confirming your organization has
                  the resources and commitment to enforce those restrictions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Explain in Part XIII how conservation easements are recorded
                  in your accounting records and financial statements. Your
                  reporting should be consistent across your books and all
                  required filings with the Internal Revenue Service.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Organizations Maintaining Collections of Art,
                  Historical Treasures, or Other Similar Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part III - Organizations Maintaining Collections of Art, Historical Treasures, or Other Similar Assets"
                    width={760}
                    height={225}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization chooses not to record the collection as
                  an asset, include in Part XIII the footnote that explains
                  financial statements that describes these collection items.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization records the collection as an asset:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any revenue related to the collection already reported
                  in Part VIII of Form 990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total value of the collection included as part of
                  your organization’s assets.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line applies if your organization holds collections
                  mainly to generate income or investment value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the revenue earned from these collections (already
                  included in total revenue on Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the asset value assigned to these collections, which
                  must also be included in total assets on Form 990 Part X.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part3-line3-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part3-line3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part3-line3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part III - Organizations Maintaining Collections of Art, Historical Treasures, or Other Similar Assets Line 3 to Line 5"
                    width={760}
                    height={160}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check all the boxes that show how your organization mainly
                  uses its{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    art, historical items, or similar collections,
                  </span>{' '}
                  based on your records.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  You can select:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Public exhibition </li>
                  <li>Scholarly research </li>
                  <li>Preservation for future generations </li>
                  <li>Loan or exchange program </li>
                  <li>Other </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  In Part XIII, explain:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li> What your collection includes, and </li>
                  <li>
                    How it supports your nonprofit mission, such as education,
                    research, or public service.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer “Yes” if your organization received donated artwork or
                  historical items mainly to sell them and raise money instead
                  of keeping them. Answer{' '}
                  <span style={{ fontWeight: 600 }}> “No”</span> if the donated
                  items were kept as part of your permanent collection.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Escrow and Custodial Arrangements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part IV reporting escrow and custodial arrangements held or managed by the organization."
                    width={760}
                    height={210}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}> “Yes”</span> if
                    your organization collects, manages, or temporarily holds
                    money or contributions for others and does
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      not treat those funds as its own assets or liabilities
                    </span>{' '}
                    on Form 990.
                    <br />
                    <span style={{ fontWeight: 600 }}> For example,</span> this
                    may happen if you collect payments and later send them to
                    creditors, beneficiaries, or partner organizations.
                  </li>
                  <li>
                    If your organization only handles its own funds, select
                    “No.”
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1">
                  If you answered “Yes,” give a short explanation in Part XIII
                  describing:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Who the money belongs to, and </li>
                  <li>How does your organization receive and distribute it.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Just briefly explain how the process works.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 1c to 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If these funds are kept in an escrow or custodial account, you
                  simply need to show how the balance moved throughout the year:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The amount in the account at the start of the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any money received or added during the year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Money paid out or sent to the correct recipients during the
                  year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of money left in the account at the end of
                  the year after all deposits have been added, and payments or
                  transfers have been made.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 2a
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Answer “Yes” if your organization holds money for others and
                    reported that amount as a liability.
                  </li>
                  <li>
                    Answer “No” if your organization did not report any such
                    amount.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 2b
                </Typography>
                <Typography variant="body1">
                  If you answered “Yes,” provide an explanation in Part XIII
                  describing:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Why your organization is holding the money, </li>
                  <li>Who the funds belong to, and </li>
                  <li>
                    What responsibility, your organization must distribute or
                    manage those funds.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If this explanation is already included in Part XIII, check
                  the box to confirm it.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V - Endowment Funds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part5-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part V, line 1, reporting the number and types of endowment funds held by the organization."
                    width={760}
                    height={192}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total value of your organization’s endowment funds
                  at the{' '}
                  <span style={{ fontWeight: 600 }}> start of each year.</span>{' '}
                  This includes permanent endowments, term endowments, and any
                  board-designated endowment funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report all amounts added to the endowment during the year.
                  This can include donor{' '}
                  <span style={{ fontWeight: 600 }}>
                    gifts, grants, transfers, or funds
                  </span>{' '}
                  your governing board decided to set aside to function like an
                  endowment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1c
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>total investment</span>{' '}
                  results for the year, including income earned from investments
                  as well as any gains or losses in value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1d
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report{' '}
                  <span style={{ fontWeight: 600 }}>amounts paid out</span> from
                  the endowment as{' '}
                  <span style={{ fontWeight: 600 }}>
                    scholarships or direct grants
                  </span>{' '}
                  to individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1e
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amounts distributed for facilities and programs.
                  Amounts should include withdrawn amounts, and amounts
                  disinvested from an organization's endowments to reduce or
                  eliminate capital investment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1f
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any costs related to managing or overseeing the
                  endowment. These may include the expenses spent to manage and
                  maintain the endowment investments
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1g
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total value of the endowment funds at the end of the
                  year after accounting. This shows how much{' '}
                  <span style={{ fontWeight: 600 }}>money remained</span> in the
                  endowment after everything that happened during the year.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part5-line2-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part5-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part5-line2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D, Part V, lines 2–4, reporting changes, balances, and details related to the organization’s endowment funds."
                    width={760}
                    height={180}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is to show{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    how your total endowment funds are divided{' '}
                  </span>{' '}
                  at the end of the current year. Use the ending balance you
                  reported on Line 1g (current year) and estimate what
                  percentage belongs to each type of endowment. The total of all
                  three percentages must be{' '}
                  <span style={{ fontWeight: 600 }}>equal to 100%.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the percentage of funds that were{' '}
                    <span style={{ fontWeight: 600 }}>
                      set aside by your organization’s board
                    </span>{' '}
                    to function like an endowment.
                  </li>
                  <li>
                    These funds are{' '}
                    <span style={{ fontWeight: 600 }}>
                      not restricted by donors,
                    </span>{' '}
                    and the board can decide to use them whenever needed.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the percentage of funds that donors require to be kept
                  invested forever. Only the income earned from these funds can
                  normally be used.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the percentage of funds that must remain invested until
                  a{' '}
                  <span style={{ fontWeight: 600 }}>
                    certain time ends, or a specific condition is met,
                  </span>{' '}
                  as required by the donor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer “Yes” if another organization that is not connected or
                  affiliated with your organization holds or manages some of
                  your endowment funds on your behalf.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> For example,</span> an
                  independent foundation or financial institution managing funds
                  for you.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if a related or affiliated organization (such as
                  a parent organization, subsidiary, or supporting organization)
                  holds or administers any of your endowment funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you answered “Yes” to Line 3a(ii), you must confirm that
                  all those related organizations are listed on{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule R (Form 990)
                  </Link>
                  . This schedule reports relationships between organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In Part XIII, explain how your organization plans to use its
                  endowment funds. You should describe the main purposes the
                  funds support.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VI - Land, Buildings, and Equipment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part6-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part6-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D, Part VI, reporting land, buildings, and equipment with cost, accumulated depreciation, and book value details."
                    width={760}
                    height={161}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You need to complete Part VI only if your organization
                  answered “Yes” on Part IV, line 11a of Form 990 and reported
                  an amount on Part X, line 10a. This section shows the value of
                  property and equipment your organization owns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the cost or original value of assets held for investment
                  purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Examples include:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Rental properties, or</li>
                  <li>Property owned mainly to earn income. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the cost or original value of property and equipment
                  used for your organization’s normal activities. 
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Examples include:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Office buildings, </li>
                  <li>Program facilities,</li>
                  <li>Equipment used for charitable or educational work. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The total of Columns (a) and (b) must match the amount
                  reported on Form 990 Part X, line 10a. 
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation recorded for these assets over
                  time. Depreciation reflects normal wear and tear or reduction
                  in value. Do not enter depreciation for land because land is
                  not depreciated.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Calculate the final value of assets by:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Adding Column (a) and Column (b), and </li>
                  <li>Subtracting Column (c). </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This gives the current recorded value of your assets. The
                  total must match the amount reported in Part X of Form 990.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VII - Investments—Other Securities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part7-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part7-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D, Part VII, listing investments in other securities with year-end balances and valuation details."
                    width={760}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Use this column to say what the investment actually is.
                    Write a clear description, such as financial derivatives,
                    closely held shares, or any other security.
                  </li>
                  <li>
                    If you are reporting publicly traded stock that meets the
                    reporting rules, include the company name, type (class) of
                    stock, and how many shares the organization owns.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the year-end value of the investment exactly as it
                  appears in your organization’s financial records. This should
                  match the amount shown in your accounting books and the
                  balance reported on Form 990. 
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this column to state how you arrived at the value entered
                  in Column (b). Simply indicate whether the investment is shown
                  at:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>the original purchase cost, or </li>
                  <li>The fair market value at the end of the year. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you report market value, make sure it is based on standard
                  and commonly accepted valuation practices so the information
                  can be clearly understood and reviewed by the Internal Revenue
                  Service.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is used to report investments in financial
                  derivatives, such as options, futures, or similar financial
                  contracts held by the organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here, the organization reports investments in closely held
                  businesses, meaning ownership interests in companies that are
                  not publicly traded.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line is used to list any other types of securities that
                  don’t fall under derivatives or closely held equity interests.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VIII - Investments—Program Related
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part8-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part8-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part VIII reporting program-related investments with year-end balances and valuation details."
                    width={760}
                    height={224}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  List each investment separately. Give a short and clear
                  explanation of what the investment is. Also mention whether it
                  is a <span style={{ fontWeight: 600 }}> loan</span> or an
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    ownership (equity) investment.
                  </span>{' '}
                  If the investment is made in a U.S. organization, include that
                  organization’s name.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of each investment based on your
                  organization’s accounting records at the end of the year. The
                  total amount reported here should match the amount shown on
                  Form 990, Part X, to keep your filing consistent with the
                  records reviewed by the Internal Revenue Service.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this column, just mention how you decided the value entered
                  in Column (b). You can indicate whether the investment is
                  shown at its original cost or at the fair market value at the
                  end of the year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IX - Other Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part9-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part9-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part9-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part IX reporting other assets not previously listed, including year-end balances and descriptions."
                    width={760}
                    height={212}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write a clear and simple description of each item included
                  under “Other Assets.” You can group or classify these assets
                  in any reasonable way that makes sense for your records for
                  example, security deposits, prepaid expenses, or other similar
                  items not listed elsewhere on the balance sheet.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    value recorded in your accounting records
                  </span>{' '}
                  for each asset listed in Column (a). When you add all the
                  amounts in this column together, the total must match the
                  amount reported on Form 990, Part X, line 15.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part X - Other Liabilities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part10-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part10-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part X - Other Liabilities"
                    width={760}
                    height={257}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to report any amounts your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    organization still owes that were not already listed
                  </span>{' '}
                  in the earlier liability lines on Form 990, Part X (lines
                  17–24). Think of this as a place to include remaining
                  obligations that don’t fit anywhere else on the form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write a short, clear note about what the organization owes and
                  why. Just use simple wording that matches your records, for
                  <span style={{ fontWeight: 600 }}> example-</span> unpaid
                  bills or money received in advance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the value recorded in your accounting records for each
                  liability listed. When you add them together, the total should
                  be the same as the Other Liabilities amount reported in Form
                  990, Part X.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your organization completes Part X, include the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      tax-related note or footnote
                    </span>{' '}
                    from your financial statements that explains any tax
                    situations that may still be uncertain or could change
                    later.
                  </li>
                  <li>
                    These rules follow accounting guidance issued by the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Financial Accounting Standards Board (FASB)
                    </span>{' '}
                    and similar standards.
                  </li>
                  <li>
                    Add the full note in Part XIII, even if there is no
                    uncertain tax liability reported. If the note covers several
                    organizations together, you can briefly summarize only your
                    organization’s portion.
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part XI-Reconciliation of Revenue per Audited Financial
                  Statements with Revenue per Return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part11-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part11-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part XI-Reconciliation of Revenue per Audited Financial Statements with Revenue per Return"
                    width={760}
                    height={246}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total income, gains, and other support shown in the audited
                  financial statements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Amounts included in the financial statements but not reported
                  as revenue on Form 990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Unrealized gains or losses from investments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Value of donated services or donated use of property or
                  facilities.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Funds recovered from grants given in earlier years.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Other adjustments (explain the details in Part XIII).{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total of lines 2a through 2d.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 2e from line 1.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Amounts reported as revenue on Form 990 but not included in
                  the audited financial statements.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4a
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Investment income included on Form 990 but not in the
                  financial statements.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Other differences (described in Part XIII).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total of lines 4a and 4b.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Final total revenue after reconciliation (must match Form 990
                  total revenue).{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part XII-Reconciliation of Expenses per Audited Financial
                  Statements with Expenses per Return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part12-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part12-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part12-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part XII-Reconciliation of Expenses per Audited Financial Statements with Expenses per Return"
                    width={760}
                    height={239}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total expenses and losses shown in the audited financial
                  statements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Expenses included in the financial statements but not reported
                  on Form 990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Donated services or donated use of facilities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Adjustments related to prior years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Other expense differences.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Additional adjustments explained in Part XIII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total of lines 2a through 2d.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 2e from line 1.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Expenses reported on Form 990 but not included in the
                  financial statements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4a
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Investment expenses reported on Form 990 but not in the
                  audited statements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Other differences (explain in Part XIII).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Total of lines 4a and 4b.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Final total expenses after reconciliation (must match Form 990
                  total expenses).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part XIII - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schd-part13-img.webp" />
                  <source srcSet="/assets/images/f990-schd-part13-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schd-part13-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule D Part XIII where organizations provide additional details to support responses given in earlier parts of Schedule D."
                    width={760}
                    height={461}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Use{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part XIII to give extra explanations
                  </span>{' '}
                  or details requested in different parts of Schedule D, such as
                  conservation easements, collections, escrow accounts,
                  endowments, uncertain tax notes, and revenue or expense
                  reconciliations. You can also use this section to{' '}
                  <span style={{ fontWeight: 600 }}>
                    clarify or add more information
                  </span>{' '}
                  anywhere needed.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule D filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Embrace the future of tax filing with{' '}
                  <strong style={{ fontWeight: 600 }}>TaxZerone</strong>, your
                  IRS-authorized e-file service provider. At TaxZerone, we
                  believe that e-filing should be simple, not stressful.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  That&apos;s why we&apos;ve designed our platform to be
                  intuitive and user-friendly, guiding you through every step
                  with unwavering support.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s how your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990 return
                  </Link>{' '}
                  with Schedule D attachment is transmitted to the IRS - just 3
                  simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule D and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule D and your 990 return to the IRS and get
                    the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We&apos;ll keep you in the loop with instant updates on your
                  form’s filing status, ensuring a hassle-free experience from
                  start to finish.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  But that&apos;s not all. If the IRS rejects your 990 return,
                  we&apos;ll help you rectify any errors and resubmit it without
                  additional cost.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Ready to attach Schedule D along with your 990 return
                        with TaxZerone?
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schd`}
                      >
                        E-File Your Form 990
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f990sd.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule D – Supplemental Financial Statements
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the purpose of filing Schedule D?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule D is used by an organization that files Form 990 to
                    provide the information required for reporting donor-advised
                    funds, conservation easements, certain art and museum
                    collections, escrow or custodial accounts or arrangements,
                    endowment funds, and supplemental financial information.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is a &quot;donor-advised fund&quot; in the context of
                  Schedule D?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Generally, a donor-advised fund is a fund or account:
                    <ol>
                      <li>
                        That is separately identified by reference to
                        contributions of a donor or donors,
                      </li>
                      <li>
                        That is owned and controlled by a sponsoring
                        organization, and
                      </li>
                      <li>
                        For which the donor or donor advisor has or reasonably
                        expects to have advisory privileges in the distribution
                        or investment of amounts held in the donor-advised fund
                        or account because of the donor&apos;s status as a
                        donor.
                      </li>
                    </ol>
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Should all tax-exempt organizations need to file Schedule
                  D?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Not necessarily. While most tax-exempt organizations are
                    required to file Form 990, including Schedule D, there are
                    exceptions. For example, certain small organizations may be
                    eligible to file{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-EZ
                    </Link>
                    , which has simplified reporting requirements and may not
                    necessitate Schedule D.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcD;
